// IR-626 — "Pages" tab on /monitoring.
//
// Renders a Page Health card per (env, page) showing:
//  - current status (green / yellow / red) derived from the latest
//    samples vs threshold + status-code rule mirrored from the
//    backend evaluator (3-consecutive-breach gate).
//  - 24h sparkline of latencies.
//  - latest sample timestamp + value.
import { StatusDot, type StatusDotVariant, StatusPill } from "@/components/iris";
import { type PageLoadGroup, type PageLoadSample, usePageLoad } from "@/hooks/use-page-load";

// Mirror of the backend default in seed_alert_categories.py
// (`AlertCategory.default_threshold`). The card's traffic light is
// derived from the LATEST 3 samples — if you change the backend
// threshold via the SPA's /alerts > Rules editor, also update this
// constant so the BE+FE display agree.
const FRONTEND_DEFAULT_THRESHOLD_MS = 30_000;
const MIN_CONSECUTIVE = 3;

interface BreachState {
  variant: StatusDotVariant;
  label: "Healthy" | "Slow" | "Unreachable" | "No data";
}

function deriveStatus(samples: PageLoadSample[]): BreachState {
  if (samples.length === 0) {
    return { variant: "idle", label: "No data" };
  }
  if (samples.length < MIN_CONSECUTIVE) {
    // Not enough data to fire — render as "healthy unknown".
    return { variant: "idle", label: "No data" };
  }
  // `samples` is ASC by sampled_at — newest is last.
  const latest = samples.slice(-MIN_CONSECUTIVE);
  const allUnreachable = latest.every((s) => s.status_code !== 200);
  const allSlow = latest.every(
    (s) => s.status_code === 200 && s.latency_ms > FRONTEND_DEFAULT_THRESHOLD_MS,
  );
  if (allUnreachable) return { variant: "err", label: "Unreachable" };
  if (allSlow) return { variant: "err", label: "Slow" };
  // Soft warn: at least one of the latest N exceeded threshold.
  const anyExceeded = latest.some(
    (s) => s.status_code !== 200 || s.latency_ms > FRONTEND_DEFAULT_THRESHOLD_MS,
  );
  if (anyExceeded) return { variant: "warn", label: "Healthy" };
  return { variant: "ok", label: "Healthy" };
}

function Sparkline({ samples }: { samples: PageLoadSample[] }) {
  if (samples.length === 0) {
    return <div style={{ color: "var(--iris-fg-4)", fontSize: 12 }}>No samples yet.</div>;
  }
  const w = 200;
  const h = 40;
  const max = Math.max(...samples.map((s) => s.latency_ms), 1);
  const dx = samples.length > 1 ? w / (samples.length - 1) : 0;
  const points = samples
    .map((s, i) => {
      const x = i * dx;
      const y = h - (s.latency_ms / max) * h;
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(" ");
  return (
    <svg width={w} height={h} aria-label="page load latency sparkline" role="img">
      <polyline fill="none" stroke="var(--iris-fg-2)" strokeWidth="1.5" points={points} />
    </svg>
  );
}

function formatLatency(latencyMs: number): string {
  if (latencyMs >= 1000) return `${(latencyMs / 1000).toFixed(2)}s`;
  return `${latencyMs}ms`;
}

function formatAge(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  return `${Math.floor(mins / 60)}h ${mins % 60}m ago`;
}

function PageHealthCard({ group }: { group: PageLoadGroup }) {
  const { variant, label } = deriveStatus(group.samples);
  const latest = group.samples[group.samples.length - 1];
  return (
    <div
      className="iris-card"
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 12,
        padding: 16,
        minWidth: 260,
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div style={{ fontWeight: 600, color: "var(--iris-fg-1)" }}>{group.page}</div>
        <StatusPill variant={group.env === "prod" ? "inactive" : "ok"}>{group.env}</StatusPill>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <StatusDot variant={variant} size={10} />
        <span style={{ color: "var(--iris-fg-2)", fontSize: 13 }}>{label}</span>
      </div>
      <Sparkline samples={group.samples} />
      {latest ? (
        <div style={{ fontSize: 12, color: "var(--iris-fg-3)" }}>
          last sample {formatAge(latest.sampled_at)} ·{" "}
          {latest.status_code === 200
            ? formatLatency(latest.latency_ms)
            : `HTTP ${latest.status_code}`}
        </div>
      ) : null}
    </div>
  );
}

export function PagesTab() {
  const { data, isLoading, error } = usePageLoad();

  if (isLoading) {
    return <div style={{ color: "var(--iris-fg-4)" }}>Loading page health…</div>;
  }
  if (error) {
    return (
      <div className="iris-card" style={{ padding: 16, color: "var(--iris-err)" }}>
        Failed to load page health samples: {(error as Error).message}
      </div>
    );
  }
  const groups = data?.data ?? [];
  if (groups.length === 0) {
    return (
      <div className="iris-card" style={{ padding: 16, color: "var(--iris-fg-4)" }}>
        No page-load samples yet. The probe runs every 5 minutes; the first sample should appear
        within ~10 minutes of the Cloud Scheduler tick being enabled.
      </div>
    );
  }
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <p style={{ color: "var(--iris-fg-3)", fontSize: 13, margin: 0 }}>
        IR-626 — Synthetic page-load probe samples for the public iris-frontend. Polls the latest
        samples every 60s; probe itself runs every 5 minutes.
      </p>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
          gap: 12,
        }}
      >
        {groups.map((g) => (
          <PageHealthCard key={`${g.env}-${g.page}`} group={g} />
        ))}
      </div>
    </div>
  );
}
